import { useContext, useState } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  PersonBeskikkelseFilterSetting,
  PersonFilterKeys,
  QueryFilter,
} from "../../../../interfaces/filters";
import {
  BeskikkelsestypeId,
  PersonBeskikkelseFilterInput,
  PersonDtoFilterInput,
} from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import DateFilter from "../../../filter/date/DateFilter";
import { defaultDateEvent } from "../../../filter/date/dateFilterHelpers";
import { DateFilterEvent } from "../../../filter/date/types";
import { KodeSelect } from "../../../formcontrols/KodeSelect";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "beskikkelse";

function enumHelper(enumString: string | undefined) {
  const found = Object.values(BeskikkelsestypeId).find((item) => {
    const casedEnum = item.replaceAll("_", "").toLowerCase();
    const casedEnumString = enumString?.toLowerCase();
    return casedEnum === casedEnumString;
  });

  return found as BeskikkelsestypeId;
}

export default function PersonBeskikkelseFilter() {
  // context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // find active settings
  const settings: PersonBeskikkelseFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // state
  const [latestDateEvent, setLatestDateEvent] =
    useState<DateFilterEvent>(defaultDateEvent);

  // stuff
  const isDateDisabled = Boolean(!settings?.activeFilters?.length);

  // handlers
  function updateFilterHandler(
    selectedItem: KodeListe | null,
    dateFilterEvent?: DateFilterEvent,
  ) {
    // get active list if any otherwise initialise an empty array
    const activeFilters = settings?.activeFilters || [];

    // add selected item if it does not exist in active list
    if (
      selectedItem &&
      !activeFilters.some((s) => s.id === enumHelper(selectedItem?.id))
    ) {
      if (selectedItem.id) {
        activeFilters.push({
          id: enumHelper(selectedItem.id),
          name: selectedItem.navn,
        });
      }
    }

    // check for valid date event otherwise assign latest date event
    if (!dateFilterEvent) {
      dateFilterEvent = latestDateEvent;
    } else {
      setLatestDateEvent(dateFilterEvent);
    }

    const filters: PersonBeskikkelseFilterInput[] = activeFilters.map((set) => {
      return {
        and: [
          {
            beskikkelsestype: {
              in: [set.id],
            },
          },
          {
            periode: dateFilterEvent?.filter,
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<PersonDtoFilterInput>()
      .addFilter("personBeskikkelse", {
        some: {
          or: filters,
        },
      })
      .build();

    const updatedSettings: PersonBeskikkelseFilterSetting = {
      activeFilters: activeFilters,
      dateFilterSettings: dateFilterEvent.settings,
    };

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      PersonBeskikkelseFilterSetting
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(
    id: PersonBeskikkelseFilterSetting["activeFilters"][number]["id"],
  ) {
    const updatedFilters =
      settings?.activeFilters?.filter((set) => {
        return id !== set.id;
      }) || [];

    const filters: PersonBeskikkelseFilterInput[] = updatedFilters.map(
      (set) => {
        return {
          and: [
            {
              beskikkelsestype: {
                in: [set.id],
              },
            },
            {
              periode: latestDateEvent?.filter,
            },
          ],
        };
      },
    );

    // build the filter string
    const filterString = f<PersonDtoFilterInput>()
      .addFilter("personBeskikkelse", {
        some: {
          or: filters,
        },
      })
      .build();

    const updatedSettings: PersonBeskikkelseFilterSetting = {
      activeFilters: updatedFilters,
      dateFilterSettings: latestDateEvent.settings,
    };

    const update = updatedSettings?.activeFilters?.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Beskikkelse"
      value="beskikkelse"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.activeFilters?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              label={filter.name}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}

      <KodeSelect
        clearAfterSelection
        kodeListeType="BeskikkelsestypeKode"
        placeholder="Vælg beskikkelse ..."
        onChange={(item) => {
          updateFilterHandler(item);
        }}
      />

      <div className="mt-3">
        <DateFilter
          handleChange={(dateFilterEvent) => {
            updateFilterHandler(null, dateFilterEvent);
          }}
          settings={settings?.dateFilterSettings}
          disabled={isDateDisabled}
          key={JSON.stringify(settings?.dateFilterSettings)}
        />
      </div>
    </AccordionFilterItem>
  );
}
